import React, { useEffect, useRef, useState } from "react";
import { UserClaimTypes, UserManager } from "app/core";
import {
  AlertMessage,
  Button,
  DropdownInput,
  IGroupValidationResult,
  Input,
  InputGroup,
  InputSizes,
  IOption,
  IPopupProps,
  ListOpenDirection,
  NotificationToast,
  Popup,
  required,
  Spinner,
  IAnswerButton,
} from "component-library";

import { PropType } from "shared/utils/types";

import {
  IAgencyList,
  INewShortlist,
  IShortlist,
  IShortlistPerformer,
  IShortlists,
} from "models";

import { IActionsFooterRowContent } from "../ActionsFooterRow";

import {
  MAX_NAME_LENGTH,
  NEW_SHORT_LIST_OPTION,
  POPUP_SIZE,
  SELECTED_SHORTLIST_ID_KEY,
  TOAST_LIFE_TIME,
  VALIDATE,
  GET_VALIDATION_DEFAULT,
  IShortListForm,
  ShortListFormValidation,
} from "./helpers";

import "./ShortlistSection.scss";

export interface IShortlistSectionProps {
  content: IActionsFooterRowContent;
  shortlists?: IShortlists;
  agencies?: IAgencyList;
  addShortList: (model: INewShortlist) => Promise<string>;
  addPerformerShortList: (
    shortlistId: number,
    model: IShortlistPerformer
  ) => Promise<string>;
}

export const ShortlistSection = ({
  content,
  shortlists,
  agencies,
  addShortList,
  addPerformerShortList,
}: IShortlistSectionProps) => {
  const [isToastVisible, setIsToastVisible] = useState(false);
  const [linkToShortList, setLinkToShortList] = useState("");
  const [model, setModel] = useState<IShortListForm>({
    isNewShortlist: false,
    shortlistName: "",
    shortlistId: NEW_SHORT_LIST_OPTION.value,
  });
  const [showSpinner, setShowSpinner] = useState(false);
  const [isShortlistPopupVisible, setIsShortlistPopupVisible] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [validationData, setValidationData] = useState<ShortListFormValidation>(
    {
      shortlistName: GET_VALIDATION_DEFAULT(),
    }
  );
  const [isValid, setIsValid] = useState(false);

  const initializedRef = useRef(false);

  const getActiveList = () => {
    return shortlists && agencies ? shortlists.shortlists : [];
  };

  useEffect(() => {
    const activeList = getActiveList();

    if (!initializedRef.current && activeList.length > 0) {
      let selectedShortListId = model.shortlistId;
      const preselectedShortlistId = parseInt(
        localStorage.getItem(SELECTED_SHORTLIST_ID_KEY) || "",
        10
      );
      selectedShortListId =
        preselectedShortlistId === NEW_SHORT_LIST_OPTION.value
          ? NEW_SHORT_LIST_OPTION.value
          : activeList.find((item) => item.id === preselectedShortlistId)
            ? preselectedShortlistId
            : activeList[0].id;

      setModel((prev) => ({
        ...prev,
        shortlistId: selectedShortListId,
      }));
      initializedRef.current = true;
    }
  }, [shortlists, agencies]);

  const getShortlistOptions = (
    newShortlistLabel: string,
    shortList: IShortlist[]
  ) => {
    const array = shortList.map(({ id: value, name }) => ({ value, name }));
    array.unshift({ ...NEW_SHORT_LIST_OPTION, name: newShortlistLabel });

    return array;
  };

  const selectShortList = (selectedOption?: IOption<number>) => {
    setModel((prev) => ({
      ...prev,
      shortlistId: selectedOption?.value,
      isNewShortlist: NEW_SHORT_LIST_OPTION.value === selectedOption?.value,
    }));

    if (selectedOption?.value === NEW_SHORT_LIST_OPTION.value) {
      setIsShortlistPopupVisible(true);
      setModel((prev) => ({ ...prev, shortlistName: "" }));
    }
  };

  const setCustomShortListName = (newName: string) => {
    setModel((prev) => ({ ...prev, shortlistName: newName }));
    VALIDATE.call();
  };

  const getFormConfiguration = () => {
    return [
      {
        name: "shortlistName",
        value: model.shortlistName,
        validators: [required(content.shortlistNameRequired)],
      },
    ];
  };

  const validationPerformed = (result: IGroupValidationResult) => {
    setValidationData((prev) => ({
      ...prev,
      ...result.validationData,
    }));
    setIsValid(!result.isGroupInvalid);
  };

  const getPerformerShortListModel = (
    agencies: IAgencyList
  ): IShortlistPerformer => {
    const [agency] = [...agencies.agents].sort(
      (left, right) => (left.position || 0) - (right.position || 0)
    );
    const [school] = [...agencies.schools].sort(
      (left, right) => (left.position || 0) - (right.position || 0)
    );
    const agencyId = agency ? agency.refNumber : school.refNumber;

    return {
      artistRef: UserManager.artistRef || "",
      publicationCode: +(UserManager.getClaim(UserClaimTypes.artistType) || ""),
      agent: agencyId,
      comment: "",
    };
  };

  const setShortList = async () => {
    if (Number(model.shortlistId) > NEW_SHORT_LIST_OPTION.value) {
      if (agencies && shortlists && !showSpinner) {
        setIsFormSubmitted(true);
        setShowSpinner(true);

        const shortlistPerformer = getPerformerShortListModel(agencies);

        let shortlistName = "";
        const { name = "" } =
          shortlists.shortlists.find(
            (shortlist) => shortlist.id === model.shortlistId
          ) || {};
        shortlistName = name;

        const link = await addPerformerShortList(
          model.shortlistId || 0,
          shortlistPerformer
        );
        setShowSpinner(false);
        setIsFormSubmitted(false);
        setIsToastVisible(true);
        setLinkToShortList(link);
        setModel((prev) => ({ ...prev, shortlistName }));
      }
    } else {
      setModel((prev) => ({ ...prev, isNewShortlist: true }));
      setIsFormSubmitted(false);
      setIsShortlistPopupVisible(true);
    }
  };

  const hideToast = () => setIsToastVisible(false);

  const renderToast = () => {
    const link = (
      <span className="c-shortlist-section__toast-text">
        {content.performerAddedToast + " "}
        <a
          className="c-shortlist-section__toast-link g-alternative-link g-alternative-link--lg"
          target="_blank"
          rel="noreferrer"
          href={linkToShortList}
          tabIndex={0}
        >
          {model.shortlistName}
        </a>
      </span>
    );

    return (
      <NotificationToast
        stickToComponent
        show={isToastVisible}
        handleHide={hideToast}
        text={link}
        timeoutDuration={TOAST_LIFE_TIME}
      />
    );
  };

  const renderSpinner = () => (
    <div className="c-shortlist-section__spinner">
      <Spinner />
    </div>
  );

  const renderPopupInput = () => (
    <InputGroup
      className="c-shortlist-section__form"
      validationDone={validationPerformed}
      triggerValidation={VALIDATE}
      inputConfigs={getFormConfiguration()}
    >
      <Input
        id="shortlistFormInput"
        label={content.newShortlistLabel}
        value={model.shortlistName}
        valueChanged={setCustomShortListName}
        type="text"
        isFormSubmitted={isFormSubmitted}
        maxLength={MAX_NAME_LENGTH}
        invalid={validationData.shortlistName.invalid}
        validationMessage={validationData.shortlistName.validationMessage}
      />
    </InputGroup>
  );

  const renderShortlistDropdown = () => {
    const activeList = getActiveList();
    const shortlistOptions = getShortlistOptions(
      content.newShortlistOption,
      activeList
    );
    const selectedOption = shortlistOptions.find(
      (option) => option.value === model.shortlistId
    );

    return (
      <DropdownInput<number>
        id="shortlistsSectionShortlist"
        variant="select"
        placeholder={content.shortlistPlaceholder}
        options={shortlistOptions}
        optionSelected={selectShortList}
        isFormSubmitted={isFormSubmitted}
        size={InputSizes.Medium}
        listOpenDirection={ListOpenDirection.Up}
        truncateOptions
        selected={selectedOption}
        searchInput={{
          show: true,
          placeholder: content.shortlistSearchPlaceholder,
        }}
      />
    );
  };

  const closeShortlistPopup = () => setIsShortlistPopupVisible(false);

  const createShortlist = async () => {
    setIsFormSubmitted(true);

    if (model.isNewShortlist && isValid && agencies && !showSpinner) {
      setShowSpinner(true);

      const shortlistPerformer = getPerformerShortListModel(agencies);

      const shortlist = {
        name: model.shortlistName || "",
        performers: [shortlistPerformer],
      };

      const link = await addShortList(shortlist);

      setShowSpinner(false);
      setIsToastVisible(true);
      setIsFormSubmitted(false);
      setIsShortlistPopupVisible(false);
      setLinkToShortList(link);
      setModel((prev) => ({ ...prev }));
    }
  };

  const wrapContent = (children: React.ReactNode) => {
    const popupSetting = {
      width: POPUP_SIZE,
      close: closeShortlistPopup,
      texts: { closePopup: "" },
      priority: "medium" as PropType<IPopupProps, "priority">,
    };

    const alertMessageSetting: {
      texts: { title: string };
      buttons: IAnswerButton[];
    } = {
      texts: {
        title: content.addToShortlistHeader,
      },
      buttons: [],
    };

    if (shortlists) {
      alertMessageSetting.buttons.push({
        name: content.saveShortlist,
        click: createShortlist,
        type: "primary",
      });
    }

    alertMessageSetting.buttons.push({
      name: content.cancelShortlist,
      click: closeShortlistPopup,
      type: "secondary",
    });

    return (
      <Popup {...popupSetting}>
        <AlertMessage {...alertMessageSetting}>{children}</AlertMessage>
      </Popup>
    );
  };

  return (
    <div className="c-shortlist-section__container g-col-lg-7 g-col-md-7 g-col-sm-12 g-inner-grid">
      {renderToast()}
      <div className="c-shortlist-section__dropdown g-col-lg-4 g-col-md-4 g-col-sm-12">
        {renderShortlistDropdown()}
      </div>
      <div className="c-shortlist-section__button g-col-lg-3 g-col-md-3 g-col-sm-12">
        <Button
          type="primary"
          size="medium"
          loading={showSpinner && !isShortlistPopupVisible}
          text={content.addToShortlistLabel}
          onClick={setShortList}
        />
      </div>
      {isShortlistPopupVisible &&
        wrapContent(showSpinner ? renderSpinner() : renderPopupInput())}
    </div>
  );
};

export default ShortlistSection;
